import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Grid, Link, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { DialogModal } from "@zfp-akademie-a-s/zfp-design";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { ChevronDownIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { CalculatorIcon } from "Components/_Shared/Icons/Icons";
import { type StateAidCalculationFormType, stateAidCalculationFormSchema } from "Forms/Schemas/Insurance/stateAidCalculationFormSchema";
import { getGetSocialSupportQueryKey, useUpdateSocialSupportCalculationParameters } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

type Props = {
  readonly analysisId: string;
  readonly versionId: string;
  readonly clientId: string;
  readonly readOnly?: boolean;
};

export const StateAidCalculationModal: FC<Props> = ({ readOnly, analysisId, versionId, clientId }) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  const [open, setOpen] = useState(false);

  const { control, handleSubmit, reset } = useForm<StateAidCalculationFormType>({
    resolver: yupResolver(stateAidCalculationFormSchema(t)),
  });

  const { mutateAsync: updateParameters } = useUpdateSocialSupportCalculationParameters({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetSocialSupportQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  const onClose = () => {
    reset();
    setOpen(false);
  };

  const onCalculate = async (data: StateAidCalculationFormType) => {
    try {
      await updateParameters({
        analysisId,
        versionId,
        clientGuid: clientId,
        data: {
          assessmentBase: {
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
            value: data.assessmentBase,
          },
          basicAmount: {
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
            value: data.basicAmount,
          },
          percentageAmount: {
            currencyCode: "CZK",
            frequency: AmountFrequency.one_time,
            value: data.percentageAmount,
          },
        },
      });
    } catch {
      toast.error(t(Resources.Common.Error));
    } finally {
      onClose();
    }
  };

  return (
    <>
      <Button
        variant="text"
        disabled={readOnly}
        sx={{ width: "fit-content" }}
        startIcon={<CalculatorIcon color="primary" />}
        endIcon={<ChevronDownIcon sx={{ fontSize: 18, color: "primary.main" }} />}
        onClick={() => setOpen(true)}
      >
        {t(Resources.AnalysisDetail.Insurance.CalculateStateAidIndividually)}
      </Button>
      <DialogModal
        open={open}
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        size="lg"
        title={t(Resources.AnalysisDetail.Insurance.StateAidCalculation)}
        subtitle={
          <>
            <Typography display="inline" variant="body1">
              {t(Resources.AnalysisDetail.Insurance.StateAidCalculationInfo1)}
            </Typography>
            <Typography display="inline" fontWeight={400}>
              <Link href="https://eportal.cssz.cz/web/portal/informativni-duchodova-aplikace" target="_blank" rel="noopener noreferrer">
                {t(Resources.AnalysisDetail.Insurance.StateAidCalculationLink)}
              </Link>
            </Typography>
            <Typography display="inline" variant="body1">
              {t(Resources.AnalysisDetail.Insurance.StateAidCalculationInfo2)}
            </Typography>
          </>
        }
        content={
          <Grid container spacing={Spacing.XL} display="flex">
            <Grid size={4}>
              <FormNumberField
                control={control}
                name="basicAmount"
                label={t(Resources.AnalysisDetail.Insurance.BasicAmount)}
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <FormNumberField
                control={control}
                name="assessmentBase"
                label={t(Resources.AnalysisDetail.Insurance.AssesmentBase)}
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                fullWidth
              />
            </Grid>
            <Grid size={4}>
              <FormNumberField
                control={control}
                name="percentageAmount"
                label={t(Resources.AnalysisDetail.Insurance.PercentageAmount)}
                scale={2}
                thousandsSeparator=" "
                unit={t(Resources.Common.CZKSuffix)}
                fullWidth
              />
            </Grid>
          </Grid>
        }
        primaryAction={
          <Button variant="contained" disabled={readOnly} onClick={() => handleSubmit(onCalculate)()}>
            {t(Resources.AnalysisDetail.Insurance.CalculateIndividually)}
          </Button>
        }
      />
    </>
  );
};
